import type { Point } from '../../core/types/point.js';
import { HoneycombTileShape } from '../types/honeycomb.js';

type FindClosestTileFn = (
  tileWidth: number,
  tilesCenters: Point[],
  mousePosition: Point,
) => number | undefined;

const SCALE = 1.15;
const FIXEDEXTENTION = 10;

const findClosestCircle: FindClosestTileFn = (
  tileWidth: number,
  tilesCenters: Point[],
  mousePosition: Point,
) => {
  const radius = (tileWidth / 2) * SCALE + FIXEDEXTENTION;

  const closestTile = tilesCenters.findIndex(
    (tile) =>
      Math.sqrt(
        Math.pow(mousePosition.x - tile.x, 2) +
          Math.pow(mousePosition.y - tile.y, 2),
      ) < radius,
  );

  return closestTile > -1 ? closestTile : undefined;
};

const findClosestHexagon: FindClosestTileFn = (
  tileWidth: number,
  tilesCenters: Point[],
  mousePosition: Point,
) => {
  const hexRadius = tileWidth / Math.sqrt(3);

  const { closestTile } = tilesCenters.reduce(
    (acc, currTile, index) => {
      const distance = Math.sqrt(
        Math.pow(mousePosition.x - currTile.x, 2) +
          Math.pow(mousePosition.y - currTile.y, 2),
      );

      if (distance < acc.closestDistance && distance <= hexRadius) {
        return {
          closestDistance: distance,
          closestTile: index,
        };
      }

      return acc;
    },
    { closestDistance: Infinity, closestTile: -1 },
  );

  return closestTile > -1 ? closestTile : undefined;
};
const findClosestSquare: FindClosestTileFn = (
  tileWidth: number,
  tilesCenters: Point[],
  mousePosition: Point,
) => {
  const halfSquare = (tileWidth / 2) * SCALE + FIXEDEXTENTION;

  const closestTile = tilesCenters.findIndex((tile) => {
    const tileBoundaries = {
      x1: tile.x - halfSquare,
      x2: tile.x + halfSquare,
      y1: tile.y - halfSquare,
      y2: tile.y + halfSquare,
    };

    const isInside =
      mousePosition.x >= tileBoundaries.x1 &&
      mousePosition.x <= tileBoundaries.x2 &&
      mousePosition.y >= tileBoundaries.y1 &&
      mousePosition.y <= tileBoundaries.y2;

    return isInside;
  });

  return closestTile > -1 ? closestTile : undefined;
};

export const findClosestTileStrategy: Record<
  HoneycombTileShape,
  FindClosestTileFn
> = {
  circle: findClosestCircle,
  hexagon: findClosestHexagon,
  square: findClosestSquare,
};
